import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Button, Container, AppBar, Toolbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState<any>({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/settings');
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  return (
    <Box>
      {/* Header */}
      <AppBar position="fixed" sx={{ background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        <Toolbar sx={{ justifyContent: 'space-between', maxWidth: 1200, mx: 'auto', width: '100%', px: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: 'white !important', fontSize: '1.8rem' }}>✨ Bella Vista Salon & Spa</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button color="inherit" href="#services" sx={{ color: 'white !important', '&:hover': { opacity: 0.8 } }}>Services</Button>
            <Button color="inherit" href="#staff" sx={{ color: 'white !important', '&:hover': { opacity: 0.8 } }}>Our Team</Button>
            <Button color="inherit" href="#contact" sx={{ color: 'white !important', '&:hover': { opacity: 0.8 } }}>Contact</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {user ? (
              <Button color="inherit" onClick={() => navigate('/dashboard')} sx={{ color: 'white !important' }}>Dashboard</Button>
            ) : (
              <>
                <Button color="inherit" onClick={() => navigate('/login')} sx={{ color: 'white !important', '&:hover': { opacity: 0.8 } }}>Login</Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/signup')} 
                  sx={{ 
                    color: 'white !important', 
                    borderColor: 'rgba(255,255,255,0.3)', 
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '25px',
                    px: 2,
                    py: 0.5,
                    ml: 1,
                    '&:hover': { background: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  Sign Up
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url("https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        textAlign: 'center',
        mt: '70px'
      }}>
        <Container>
          <Typography variant="h2" fontWeight={700} gutterBottom sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)', color: 'white !important' }}>
            Welcome to Bella Vista
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, maxWidth: 600, mx: 'auto', color: 'white' }}>
            Experience luxury beauty and wellness services in a serene, professional environment. Your journey to radiance begins here.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" onClick={() => navigate('/book-appointment')} 
              sx={{ px: 4, py: 2, borderRadius: '30px', background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)' }}>
              Book Appointment
            </Button>
            <Button variant="outlined" size="large" 
              sx={{ px: 4, py: 2, borderRadius: '30px', color: 'white', borderColor: 'white' }}>
              Explore Services
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Box id="services" sx={{ py: 8, bgcolor: '#f8f9fa' }}>
        <Container>
          <Typography variant="h3" textAlign="center" color="#E91E63" gutterBottom>
            Our Premium Services
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              { icon: '✂️', title: 'Hair Services', desc: 'Professional haircut, coloring, styling, and treatments for all hair types. From classic cuts to modern trends.' },
              { icon: '🧴', title: 'Facial Treatments', desc: 'Rejuvenating facials, anti-aging treatments, and skincare solutions tailored to your skin\'s unique needs.' },
              { icon: '💅', title: 'Nail Care', desc: 'Manicures, pedicures, gel applications, and nail art. Complete hand and foot care in a relaxing environment.' },
              { icon: '💄', title: 'Makeup Services', desc: 'Professional makeup for special occasions, bridal services, and makeup consultations with expert artists.' },
              { icon: '🌿', title: 'Spa Treatments', desc: 'Relaxing massages, body treatments, and wellness therapies to rejuvenate your mind and body.' },
              { icon: '💎', title: 'Premium Packages', desc: 'Comprehensive beauty packages combining multiple services for the ultimate pampering experience.' }
            ].map((service, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 3, borderRadius: '15px', boxShadow: '0 5px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-5px)' } }}>
                  <CardContent>
                    <Typography variant="h2" sx={{ mb: 2 }}>{service.icon}</Typography>
                    <Typography variant="h5" gutterBottom color="#E91E63" fontWeight={600}>
                      {service.title}
                    </Typography>
                    <Typography color="text.secondary">{service.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Staff Section */}
      <Box id="staff" sx={{ py: 8, bgcolor: 'white' }}>
        <Container>
          <Typography variant="h3" textAlign="center" color="#E91E63" gutterBottom>
            Meet Our Expert Team
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              { name: 'Maya Rodriguez', role: 'Senior Stylist', desc: '10+ years of experience in hair cutting, coloring, and styling. Specializes in modern cuts and color techniques.', img: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
              { name: 'Alex Chen', role: 'Hair Specialist', desc: 'Expert in hair treatments and balayage techniques. Passionate about creating personalized looks for each client.', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
              { name: 'Sophia Williams', role: 'Spa Therapist', desc: 'Licensed aesthetician specializing in facial treatments, chemical peels, and therapeutic massage services.', img: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
              { name: 'James Brown', role: 'Nail Technician', desc: 'Certified nail artist with expertise in manicures, pedicures, and creative nail designs for all occasions.', img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' }
            ].map((staff, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f8f9fa', p: 3, borderRadius: '15px', '&:hover': { transform: 'translateY(-5px)' } }}>
                  <Box sx={{ width: 150, height: 150, borderRadius: '50%', mx: 'auto', mb: 2, backgroundImage: `url(${staff.img})`, backgroundSize: 'cover', backgroundPosition: 'center', border: '4px solid #E91E63' }} />
                  <Typography variant="h6" fontWeight={600}>{staff.name}</Typography>
                  <Typography variant="subtitle1" color="#E91E63" fontWeight={600}>{staff.role}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">{staff.desc}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Special Offers */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)', color: 'white', textAlign: 'center' }}>
        <Container>
          <Typography variant="h3" gutterBottom color="white">Special Offers</Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              { title: '🎉 New Client Special', desc: '20% off your first visit! Experience our premium services at an introductory rate.', btn: 'Book Now' },
              { title: '💅 Spa Package Deal', desc: 'Facial + Massage + Manicure for only $199. Save $50 on this relaxing combination.', btn: 'Book Package' },
              { title: '👰 Bridal Services', desc: 'Complete bridal beauty packages including trial sessions. Make your special day perfect.', btn: 'Inquire Now' }
            ].map((offer, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', p: 3, borderRadius: '15px', border: '1px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(10px)' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom color="white">{offer.title}</Typography>
                    <Typography sx={{ mb: 3, color: 'white' }}>{offer.desc}</Typography>
                    <Button variant="outlined" sx={{ color: 'white', borderColor: 'white' }} onClick={() => navigate('/login')}>
                      {offer.btn}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box sx={{ py: 8, bgcolor: 'white' }}>
        <Container>
          <Typography variant="h3" textAlign="center" color="#E91E63" gutterBottom>
            What Our Clients Say
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              { stars: '⭐⭐⭐⭐⭐', text: 'Absolutely amazing experience! Maya gave me the perfect haircut and the spa treatment was so relaxing. Highly recommend!', author: 'Sarah Johnson' },
              { stars: '⭐⭐⭐⭐⭐', text: 'The bridal package was perfect for my wedding day. Professional, friendly staff and beautiful results. Thank you!', author: 'Emily Davis' },
              { stars: '⭐⭐⭐⭐⭐', text: 'Best salon in town! Clean, modern facility with skilled professionals. My nails have never looked better.', author: 'Maria Garcia' }
            ].map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ bgcolor: '#f8f9fa', p: 3, borderRadius: '15px', borderLeft: '4px solid #E91E63' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: '#ffc107' }}>{testimonial.stars}</Typography>
                    <Typography sx={{ mb: 2 }} color="text.primary">"{testimonial.text}"</Typography>
                    <Typography fontWeight={600} color="text.primary">- {testimonial.author}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box id="contact" sx={{ bgcolor: '#333', color: 'white', py: 6 }}>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white !important' }}>Contact Information</Typography>
              <Typography color="white">📧 {settings.salon_email || 'info@bellavistaspa.com'}</Typography>
              <Typography color="white">📞 {settings.salon_phone || '(555) 123-4567'}</Typography>
              <Typography color="white">🕒 Mon-Sat: 9AM-8PM, Sun: 10AM-6PM</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white !important' }}>Quick Links</Typography>
              <Typography><Button color="inherit" sx={{ color: 'white' }} onClick={() => navigate('/login')}>Book Appointment</Button></Typography>
              <Typography><Button color="inherit" sx={{ color: 'white' }} onClick={() => navigate('/login')}>Client Login</Button></Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white !important' }}>Follow Us</Typography>
              <Typography color="white">Stay updated with our latest offers and beauty tips!</Typography>
              <Typography color="white">📱 Download our app for easy booking</Typography>
            </Grid>
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4, pt: 4, borderTop: '1px solid #555' }}>
            <Typography color="white">&copy; 2024 Bella Vista Salon & Spa. All rights reserved.</Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;